import { AnNamElement } from '../../base-element';

/**
 * an-scroll-slider — horizontal overflow-scroll container with:
 *   - External prev/next button connections
 *
 * Attributes:
 *   prev-btn  CSS selector for the external "previous" <button> element
 *   next-btn  CSS selector for the external "next" <button> element
 *
 * CSS custom properties (override per use-site):
 *   --an-scroll-slider-gap          gap between slides  (mobile: 1.2rem, tablet+: 1.6rem)
 *
 * Expected Liquid structure:
 *   <an-scroll-slider prev-btn="#prev-id" next-btn="#next-id">
 *     <div class="an-scroll-slider__track">
 *       <!-- slides -->
 *     </div>
 *   </an-scroll-slider>
 */
export class AnScrollSlider extends AnNamElement {
  // Suppress Lit render — Liquid renders the track and slides
  protected override scheduleUpdate(): void {}

  private _track: HTMLElement | null = null;
  private _prevBtn: HTMLButtonElement | null = null;
  private _nextBtn: HTMLButtonElement | null = null;

  // Item width for step-based scrolling
  private _itemWidth = 0;

  connectedCallback(): void {
    super.connectedCallback();
    this._track = this.querySelector<HTMLElement>('.an-scroll-slider__track');
    if (!this._track) return;

    this._connectButtons();
    this._bindTrackEvents();
    this._updateItemWidth();
    this._updateState();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._prevBtn?.removeEventListener('click', this._onPrevClick);
    this._nextBtn?.removeEventListener('click', this._onNextClick);
    this._unbindTrackEvents();
  }

  /**
   * Re-read slide item positions after DOM changes (e.g. cart update, filter change).
   * Call this if the track's child slides are re-rendered.
   */
  resetPages(): void {
    this._updateItemWidth();
    this._updateState();
  }

  private _connectButtons(): void {
    const prevSel = this.getAttribute('prev-btn');
    const nextSel = this.getAttribute('next-btn');
    if (prevSel) {
      this._prevBtn = document.querySelector<HTMLButtonElement>(prevSel);
      this._prevBtn?.addEventListener('click', this._onPrevClick);
    }
    if (nextSel) {
      this._nextBtn = document.querySelector<HTMLButtonElement>(nextSel);
      this._nextBtn?.addEventListener('click', this._onNextClick);
    }
  }

  private _bindTrackEvents(): void {
    if (!this._track) return;
    this._track.addEventListener('scroll', this._onScroll, { passive: true });
  }

  private _unbindTrackEvents(): void {
    if (this._track) {
      this._track.removeEventListener('scroll', this._onScroll);
    }
  }

  private _updateItemWidth(): void {
    const slides = this._track?.querySelectorAll<HTMLElement>('[id^="Slide-"]');
    if (!slides || slides.length < 2) return;
    // Use the first two visible slides to determine item width
    const visible = Array.from(slides).filter((el) => el.clientWidth > 0);
    if (visible.length >= 2) {
      this._itemWidth = visible[1].offsetLeft - visible[0].offsetLeft;
    }
  }

  private _updateState(): void {
    if (!this._track) return;
    const { scrollLeft, scrollWidth, clientWidth } = this._track;
    // 1px tolerance for subpixel rounding (scrollLeft is fractional in modern browsers)
    const hasLeft = scrollLeft > 1;
    const hasRight = scrollLeft + clientWidth < scrollWidth - 1;

    this.classList.toggle('has-fade-left', hasLeft);
    this.classList.toggle('has-fade-right', hasRight);

    if (this._prevBtn) this._prevBtn.disabled = !hasLeft;
    if (this._nextBtn) this._nextBtn.disabled = !hasRight;
  }

  private _scrollStep(btn: HTMLButtonElement): number {
    const step = parseInt(btn.dataset.step ?? '1') || 1;
    return step * (this._itemWidth > 0 ? this._itemWidth : (this._track?.clientWidth ?? 300) * 0.8);
  }

  // ── Scroll listener ──────────────────────────────────────────────────────

  private _onScroll = (): void => {
    this._updateState();
  };

  // ── External button handlers ─────────────────────────────────────────────

  private _onPrevClick = (e: Event): void => {
    const btn = e.currentTarget as HTMLButtonElement;
    this._track?.scrollBy({ left: -this._scrollStep(btn), behavior: 'smooth' });
  };

  private _onNextClick = (e: Event): void => {
    const btn = e.currentTarget as HTMLButtonElement;
    this._track?.scrollBy({ left: this._scrollStep(btn), behavior: 'smooth' });
  };
}

if (!customElements.get('an-scroll-slider')) {
  customElements.define('an-scroll-slider', AnScrollSlider);
}
